// Mobile turn bar — compact bottom bar for turn info and actions on small screens

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import { ACTION_TYPE_ORDER, actionTypeLabel, renderActionIcon } from './action-icons.js';

/**
 * @typedef {import('../../types/jsdoc-types.js').TurnState} TurnState
 * @typedef {import('../../types/jsdoc-types.js').CharacterAction} CharacterAction
 */

/**
 * Mobile turn bar — fixed at the bottom of the game view.
 * Shows mode, turn info, movement, action/bonus buttons, and end turn.
 *
 * Fires: end-turn, start-targeting, execute-action, start-bonus-action
 */
class MobileTurnBar extends BaseComponent {
  static properties = {
    show: { type: Boolean },
    turnState: { type: Object },
    currentUserId: { type: String, attribute: 'current-user-id' },
    actions: { type: Array },
    playerCharacters: { type: Array },
    _showActionSheet: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .turn-bar {
      display: flex;
      flex-direction: column;
      background: rgba(0, 0, 0, 0.9);
      border-top: 1px solid #444;
      backdrop-filter: blur(4px);
      color: white;
    }

    .turn-bar.your-turn.exploration {
      background: rgba(59, 130, 246, 0.95);
    }

    .turn-bar.your-turn.combat {
      background: rgba(220, 38, 38, 0.95);
    }

    .turn-bar.combat:not(.your-turn) {
      background: rgba(127, 29, 29, 0.9);
    }

    .status-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
    }

    .status-label {
      font-size: 0.7rem;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      opacity: 0.8;
      display: flex;
      align-items: center;
      gap: 3px;
    }

    .actions-row {
      display: flex;
      gap: 6px;
      padding: 0 10px 8px;
    }

    .action-btn {
      flex: 1;
      padding: 6px 6px;
      border-radius: 6px;
      border: 1px solid rgba(255, 255, 255, 0.25);
      background: rgba(255, 255, 255, 0.12);
      color: white;
      font-size: 0.75rem;
      font-weight: 600;
      text-align: center;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
    }

    .action-btn svg {
      vertical-align: -2px;
      margin-right: 2px;
    }

    .action-btn:active:not(.disabled) {
      background: rgba(255, 255, 255, 0.25);
    }

    .action-btn.disabled {
      opacity: 0.4;
      cursor: default;
    }

    .action-btn.end-turn {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.4);
    }

    /* Action sheet overlay */
    .action-sheet-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.4);
      z-index: 10000;
      display: flex;
      align-items: flex-end;
    }

    .action-sheet {
      width: 100%;
      background: #1a1a1a;
      border-top: 1px solid #444;
      border-radius: 12px 12px 0 0;
      max-height: 60vh;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }

    .action-sheet-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      border-bottom: 1px solid #333;
    }

    .action-sheet-title {
      font-size: 1rem;
      font-weight: 600;
      color: white;
    }

    .action-sheet-close {
      background: none;
      border: none;
      color: #6ea8fe;
      font-size: 0.9rem;
      cursor: pointer;
      padding: 4px 8px;
      -webkit-tap-highlight-color: transparent;
    }

    .action-sheet-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 16px;
      border-bottom: 1px solid #2a2a2a;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
    }

    .action-sheet-item:active {
      background: #2a2a2a;
    }

    .action-sheet-item:last-child {
      border-bottom: none;
    }

    .action-name {
      font-size: 0.95rem;
      color: white;
    }

    .action-chevron {
      color: #555;
    }

    .action-group-header {
      padding: 10px 16px 6px;
      font-size: 15px;
      font-weight: 600;
      color: white;
      background: #111;
      border-bottom: 1px solid #222;
    }

    .sheet-action-row {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .sheet-action-icon {
      width: 22px;
      height: 22px;
      flex: 0 0 22px;
      color: rgba(255, 255, 255, 0.85);
    }

    .sheet-action-icon svg {
      width: 100%;
      height: 100%;
    }

    .sheet-action-meta {
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: 2px;
      min-width: 0;
    }

    .sheet-action-cost {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.6);
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    .sheet-action-description {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.65);
      font-style: italic;
    }

    .sheet-action-disabled-icon {
      width: 18px;
      height: 18px;
      flex: 0 0 18px;
      color: rgba(255, 255, 255, 0.7);
      cursor: help;
      margin-left: auto;
    }

    .sheet-action-disabled-icon svg {
      width: 100%;
      height: 100%;
    }

    .sheet-action-summary {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.8);
      margin-top: 4px;
    }

    .waiting-label {
      padding: 10px 12px;
      text-align: center;
      font-size: 0.85rem;
      opacity: 0.8;
    }
  `;

  constructor() {
    super();
    this.show = false;
    this.turnState = null;
    this.currentUserId = '';
    /** @type {CharacterAction[]} */
    this.actions = [];
    this.playerCharacters = [];
    this._showActionSheet = false;
  }

  get _isYourTurn() {
    return this.turnState?.currentPlayerID === this.currentUserId;
  }

  get _yourResources() {
    return this.turnState?.turnResources?.[this.currentUserId] || null;
  }

  // Always-on initiative (#358): three states map to flat fields on
  // turnState. _isCombatTime drives the bar coloring; _isHostileCombat
  // adds the red treatment.
  get _isCombatTime() {
    return !!this.turnState?.combatTime;
  }

  get _isHostileCombat() {
    return !!this.turnState?.hostileVisible;
  }

  get _currentActorName() {
    const order = this.turnState?.initiativeOrder;
    const idx = this.turnState?.currentIndex ?? 0;
    return order?.[idx]?.name || '';
  }

  _hourglassIcon() {
    return html`<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 22h14" /><path d="M5 2h14" /><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" /><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" /></svg>`;
  }

  _footprintsIcon() {
    return html`<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 16v-2.38C4 11.5 2.97 10.5 3 8c.03-2.72 1.49-6 4.5-6C9.37 2 10 3.8 10 5.5c0 3.11-2 5.66-2 8.68V16a2 2 0 1 1-4 0Z" /><path d="M20 20v-2.38c0-2.12 1.03-3.12 1-5.62-.03-2.72-1.49-6-4.5-6C14.63 6 14 7.8 14 9.5c0 3.11 2 5.66 2 8.68V20a2 2 0 1 0 4 0Z" /></svg>`;
  }

  _mapIcon() {
    return html`<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" /><line x1="9" y1="3" x2="9" y2="18" /><line x1="15" y1="6" x2="15" y2="21" /></svg>`;
  }

  _swordsIcon() {
    return html`<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" /><line x1="13" x2="19" y1="19" y2="13" /><line x1="16" x2="20" y1="16" y2="20" /><line x1="19" x2="21" y1="21" y2="19" /><polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5" /><line x1="5" x2="9" y1="14" y2="18" /><line x1="7" x2="4" y1="17" y2="20" /><line x1="3" x2="5" y1="19" y2="21" /></svg>`;
  }

  _openCircle() {
    return html`<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" /></svg>`;
  }

  _checkCircle() {
    return html`<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>`;
  }

  _handleEndTurn() {
    this.dispatchEvent(new CustomEvent('end-turn', { bubbles: true }));
  }

  /** @param {CharacterAction} action */
  _handleAction(action) {
    this._showActionSheet = false;
    if (action.targeting) {
      this.dispatchEvent(new CustomEvent('start-targeting', { bubbles: true, detail: action }));
    } else {
      this.dispatchEvent(new CustomEvent('execute-action', { bubbles: true, detail: action }));
    }
  }

  render() {
    if (!this.show || !this.turnState) return html``;

    const res = this._yourResources;
    const modeClass = this._isCombatTime ? 'combat' : 'exploration';
    const hostileClass = this._isHostileCombat ? 'hostile' : '';
    const yourTurnClass = this._isYourTurn ? 'your-turn' : '';

    // Not your turn — compact waiting state. Always-on initiative (#358):
    // when an unseen monster is acting, the server neutralizes
    // currentActorID to "" so the bar renders "Combat continues…"
    // rather than leaking the unseen actor's name.
    if (!this._isYourTurn) {
      let waitingFor;
      if (this._isCombatTime && this._currentActorName) {
        waitingFor = `${this._currentActorName}'s turn`;
      } else if (this._isCombatTime) {
        waitingFor = 'Combat continues…';
      } else {
        waitingFor = 'Waiting...';
      }
      return html`
        <div class="turn-bar ${modeClass} ${hostileClass} ${yourTurnClass}">
          <div class="waiting-label">${waitingFor}</div>
        </div>
      `;
    }

    return html`
      <div class="turn-bar ${modeClass} ${hostileClass} ${yourTurnClass}">
        <div class="status-row">
          <span class="status-label">${this._hourglassIcon()} Your Turn</span>
          ${res ? html`<span class="status-label">${this._footprintsIcon()} ${res.movementRemaining}ft</span>` : ''}
          <span class="status-label">${this._isCombatTime ? this._swordsIcon() : this._mapIcon()} ${this._isHostileCombat ? 'Combat' : this._isCombatTime ? 'Initiative' : 'Exploration'}</span>
        </div>
        <div class="actions-row">
          <button
            class="action-btn"
            ?disabled="${!this.actions?.some((a) => a.enabled)}"
            @click="${() => {
              if (this.actions?.some((a) => a.enabled)) this._showActionSheet = true;
            }}"
          >${this._openCircle()} Actions</button>
          <span class="resource-pips">
            <span class="pip ${res?.actionUsed ? 'spent' : ''}">Action</span>
            <span class="pip ${res?.bonusActionUsed ? 'spent' : ''}">Bonus</span>
            <span class="pip">Reaction</span>
            <span class="pip ${res?.objectInteractionUsed ? 'spent' : ''}">Interaction</span>
          </span>
          <button class="action-btn end-turn" @click="${this._handleEndTurn}">End Turn</button>
        </div>
      </div>

      ${this._showActionSheet ? this._renderActionSheet() : ''}
    `;
  }

  _renderActionSheet() {
    /** @type {Map<string, CharacterAction[]>} */
    const groups = new Map();
    for (const action of this.actions || []) {
      const key = /** @type {any} */ (action).actionType || 'special';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(action);
    }
    for (const list of groups.values()) {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }
    const orderedTypes = ACTION_TYPE_ORDER.filter((t) => groups.has(t));
    return html`
      <div class="action-sheet-backdrop" @click="${() => {
        this._showActionSheet = false;
      }}">
        <div class="action-sheet" @click="${(/** @type {Event} */ e) => e.stopPropagation()}">
          <div class="action-sheet-header">
            <span class="action-sheet-title">Actions</span>
            <button class="action-sheet-close" @click="${() => {
              this._showActionSheet = false;
            }}">Cancel</button>
          </div>
          ${orderedTypes.map(
            (type) => html`
            <div class="action-group-header">${actionTypeLabel(type)}</div>
            ${groups.get(type).map((action) => this._renderActionSheetItem(action))}
          `,
          )}
        </div>
      </div>
    `;
  }

  /** @param {any} action */
  _renderActionSheetItem(action) {
    const cost = action.costs?.[0]?.label;
    return html`
      <div class="action-sheet-item ${action.enabled ? '' : 'disabled'}" @click="${() => action.enabled && this._handleAction(action)}">
        <div class="sheet-action-row">
          <span class="sheet-action-icon">${renderActionIcon(action.icon)}</span>
          <div class="sheet-action-meta">
            <span class="action-name">${action.name}</span>
            ${cost ? html`<span class="sheet-action-cost">${cost}</span>` : ''}
            ${action.description ? html`<span class="sheet-action-description">${action.description}</span>` : ''}
            ${
              action.actionSummary
                ? html`
              <span class="sheet-action-summary">${action.actionSummary}</span>
            `
                : ''
            }
          </div>
          ${
            !action.enabled && action.disabledReason
              ? html`<span
                class="sheet-action-disabled-icon"
                data-bs-toggle="tooltip"
                data-bs-title="${action.disabledReason}"
              >${renderActionIcon('ban')}</span>`
              : ''
          }
          <i class="action-chevron bi bi-chevron-right"></i>
        </div>
      </div>
    `;
  }
}

customElements.define('mobile-turn-bar', MobileTurnBar);
